<template>
  <div class="vcr-autoline">
    <div class="vcr-autoline__title">
      <h1>{{ i18n.t('/Vcr/Autoline/Title') }}</h1>
      <span>{{ i18n.t('/Vcr/Autoline/Subtitle') }}</span>
    </div>

    <section class="vcr-autoline__meta">
      <dl>
        <dt>{{ i18n.t('/Vcr/Meta Info/Quote Number Label') }}</dt>
        <dd data-qa="vcr-quote-number">{{ $route.params.quoteId }}</dd>
        <dt>{{ i18n.t('/Vcr/Autoline/Status Label') }}</dt>
        <dd>{{ statusLabel }}</dd>
        <dt>{{ i18n.t('/Vcr/Meta Info/Delivery Date Label') }}</dt>
        <dd>{{ formatDate(deliveryDate) }}</dd>
        <dt>{{ i18n.t('/Vcr/Meta Info/Offer Valid Label') }}</dt>
        <dd>{{ formatDate(expireDate) }}</dd>
      </dl>
      <dl>
        <dt>{{ i18n.t('/Vcr/Autoline/Customer Name Label') }}</dt>
        <dd data-qa="vcr-customer-name">
          {{ customerName }}
          <small v-if="contactPersonName">{{ contactPersonName }}</small>
        </dd>
        <dt>{{ i18n.t('/Vcr/Contact Details/Customer/Customer Type Label') }}</dt>
        <dd>{{ customerTypeLabel }}</dd>
        <dt>{{ i18n.t('/Vcr/Autoline/Employee Label') }}</dt>
        <dd>{{ employee?.EmployeeName || '-' }}</dd>
        <dt>{{ i18n.t('/Vcr/Autoline/Establishment Label') }}</dt>
        <dd>{{ dealer?.DealerName || '-' }}</dd>
      </dl>
      <dl>
        <dt>{{ i18n.t('/Vcr/Autoline/Model Label') }}</dt>
        <dd data-qa="vcr-model-name">{{ configuration?.ModelName || '-' }}</dd>
        <dt>{{ i18n.t('/Vcr/Autoline/Trimline Label') }}</dt>
        <dd>{{ configuration?.TrimlineName || '-' }}</dd>
        <dt>{{ vehicleCodeLabel }}</dt>
        <dd data-qa="vcr-vehicle-code">{{ vehicleCode || '-' }}</dd>
      </dl>
    </section>

    <div class="vcr-autoline__ledgers">
      <vcr-ledger
        :title="i18n.t('/Vcr/Autoline/Vehicle Title')"
        :tag="vehicleCode"
        :groups="vehicleGroups"
      />
      <vcr-ledger :title="i18n.t('/Vcr/Autoline/Costs Title')" :groups="costGroups" />
    </div>

    <section class="vcr-autoline__total" data-qa="vcr-total">
      <h3>{{ i18n.t('/Vcr/Autoline/Total Title') }}</h3>
      <dl>
        <dt>{{ i18n.t('/Vcr/Autoline/Buy Label') }}</dt>
        <dd data-qa="vcr-purchase-price">{{ CurrencyHelper.currency(totalPrice.PurchaseExcl) }}</dd>
        <dt>{{ i18n.t('/Vcr/Autoline/Sell Label') }}</dt>
        <dd data-qa="vcr-selling-price">{{ CurrencyHelper.currency(totalPrice.Excl) }}</dd>
        <dt>{{ i18n.t('/Vcr/Autoline/Margin Label') }}</dt>
        <dd class="vcr-autoline__total-margin" data-qa="vcr-margin-price">
          {{ CurrencyHelper.margin(totalPrice.MarginExcl) }}
          <vcr-margin-percentage
            data-qa="vcr-margin-percentage"
            :percentage="totalPrice.MarginPercentage"
          />
        </dd>
      </dl>
      <dl>
        <dt>{{ i18n.t('/Vcr/Autoline/Btw Label') }}</dt>
        <dd>{{ CurrencyHelper.currency(totalPrice.Btw) }}</dd>
        <dt>{{ i18n.t('/Vcr/Autoline/Bpm Label') }}</dt>
        <dd>{{ CurrencyHelper.currency(totalPrice.Bpm) }}</dd>
        <dt>{{ i18n.t('/Vcr/Autoline/Total Incl Label') }}</dt>
        <dd>{{ CurrencyHelper.currency(totalPrice.Incl) }}</dd>
      </dl>
    </section>

    <div class="vcr-autoline__panels">
      <section v-if="hasExchangeCars" class="vcr-autoline__panel vcr-autoline__panel--wide">
        <h3>{{ i18n.t('/Vcr/Exchange Car/Title') }}</h3>
        <table>
          <thead>
            <tr>
              <th>{{ i18n.t('/Vcr/Exchange Car/License Plate Label') }}</th>
              <th>{{ i18n.t('/Vcr/Exchange Car/Brand Label') }}</th>
              <th>{{ i18n.t('/Vcr/Exchange Car/Model Label') }}</th>
              <th>{{ i18n.t('/Vcr/Exchange Car/Milage Label') }}</th>
              <th>{{ i18n.t('/Vcr/Exchange Car/Car Type Label') }}</th>
              <th class="r">{{ i18n.t('/Vcr/Exchange Car/Trade In Value Label') }}</th>
              <th class="r">{{ i18n.t('/Vcr/Exchange Car/Price Incl Label') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(car, index) in exchangeCars" :key="index">
              <td>{{ car.LicensePlate }}</td>
              <td>{{ car.BrandName }}</td>
              <td>{{ car.ModelName }}</td>
              <td>{{ car.Mileage }}</td>
              <td>{{ car.Type }}</td>
              <td class="r">{{ CurrencyHelper.currency(car.TradeValue) }}</td>
              <td class="r">{{ CurrencyHelper.currency(car.Price?.Incl) }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section v-if="fleetCompany" class="vcr-autoline__panel">
        <h3>{{ i18n.t('/Vcr/Contact Details/FleetCompany/Title') }}</h3>
        <dl>
          <template v-for="[label, value] in fleetContractFields" :key="label">
            <dt>{{ i18n.t(`/Vcr/Contact Details/FleetCompany/${label}`) }}</dt>
            <dd>{{ value || '-' }}</dd>
          </template>
        </dl>
      </section>

      <section v-if="privateLeaseDiscounts" class="vcr-autoline__panel" data-qa="vcr-private-lease">
        <h3>{{ i18n.t('/Vcr/Private Lease/Title') }}</h3>
        <dl>
          <template v-for="[label, value] in privateLeaseDiscountFields" :key="label">
            <dt>{{ i18n.t(`/Vcr/Private Lease/${label}`) }}</dt>
            <dd>{{ value || '-' }}</dd>
          </template>
        </dl>
      </section>

      <section class="vcr-autoline__panel vcr-autoline__signature">
        <h3>{{ i18n.t('/Vcr/Signature/Sign Label') }}</h3>
        <div>
          <span>{{ i18n.t('/Vcr/Signature/Name Label') }}</span>
          <span>{{ i18n.t('/Vcr/Signature/Date Label') }}</span>
          <span>{{ i18n.t('/Vcr/Signature/Signature Label') }}</span>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import CurrencyHelper from '@/helpers/currencyHelper'
import { getStatus } from '@/helpers/statusHelper'
import VcrLedger from './VcrLedger.vue'
import VcrMarginPercentage from './VcrMarginPercentage.vue'
import { configurationRows, ledgerTotal, quoteItemRows, toLedgerRow } from './vcrLedgerRows'

export default {
  components: { VcrLedger, VcrMarginPercentage },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      CurrencyHelper,
    }
  },
  computed: {
    ...mapGetters({
      status: 'quote/status',
      deliveryDate: 'quote/deliveryDate',
      expireDate: 'quote/expireDate',
      isStockCar: 'quote/isStockCar',
      hasCustomer: 'quote/hasCustomer',
      customer: 'quote/customer',
      customerCompany: 'quote/customerCompany',
      hasCustomerCompany: 'quote/hasCustomerCompany',
      leaseCompany: 'quote/leaseCompany',
      hasLeaseCompany: 'quote/hasLeaseCompany',
      employee: 'quote/employee',
      dealer: 'quote/dealer',
      customerType: 'quote/customerType',
      hasCustomerType: 'quote/hasCustomerType',
      controlCodes: 'quote/controlCodes',
      accessoriesWithCategories: 'quote/accessoriesWithCategories',
      totalPriceGetter: 'quote/totalPrice',
      exchangeCars: 'quote/exchangeCars',
      hasExchangeCars: 'quote/hasExchangeCars',
      fleetContract: 'quote/fleetContract',
      showPrivateLease: 'quote/showVwpfsPrivateLeasePdf',
      latestVwpfsPrivateLeaseCall: 'quote/latestVwpfsPrivateLeaseCall',
      configuration: 'configurationExtended/configurationExtended',
      komNumber: 'configurationExtended/komNumber',
    }),
    totalPrice() {
      return this.totalPriceGetter || {}
    },
    statusLabel() {
      return getStatus(this.status)
    },
    // The company is the customer; a private customer on a company quote is its contact person.
    customerName() {
      if (this.hasCustomerCompany) return this.customerCompany.CustomerCompanyName
      if (this.hasLeaseCompany) return this.leaseCompany.Name
      return this.personName || '-'
    },
    contactPersonName() {
      return this.hasCustomerCompany || this.hasLeaseCompany ? this.personName : null
    },
    personName() {
      if (!this.hasCustomer) return null
      const { CustomerInitials, CustomerMiddleName, CustomerFamilyName } = this.customer
      return [CustomerInitials, CustomerMiddleName, CustomerFamilyName].filter(Boolean).join(' ')
    },
    customerTypeLabel() {
      if (!this.hasCustomerType) return '-'
      return `${this.customerType.GroupName} - ${this.customerType.Name}`
    },
    vehicleCodeLabel() {
      return this.isStockCar
        ? this.i18n.t('/Vcr/Autoline/Kom Number Label')
        : this.i18n.t('/Vcr/Autoline/Configuration Code Label')
    },
    vehicleCode() {
      return this.isStockCar ? this.komNumber : this.configuration?.ConfigurationCode
    },
    vehicleGroups() {
      return [
        this.configurationGroup(),
        this.group('Control Codes', quoteItemRows(this.controlCodes, 'ShortId')),
      ]
    },
    costGroups() {
      return [
        this.categoryGroup('Delivery Costs', 'DeliveryCosts'),
        this.categoryGroup('Dealer Accessories', 'DealerAccessories'),
        this.categoryGroup('Corrections', 'Corrections'),
        this.categoryGroup('Bonuses', 'Bonuses'),
        this.categoryGroup('Non Recoverable Costs', 'NonRecoverableCosts'),
      ]
    },
    fleetCompany() {
      return this.fleetContract?.CustomerFleetCompany
    },
    fleetContractFields() {
      const contract = this.fleetContract
      return [
        ['Name Label', this.fleetCompany.BusinessName],
        ['Contract Number Label', contract.ContractNumber],
        ['Deal Type Label', contract.KindOfDeal],
        ['Discount Structure Label', contract.DiscountStructure],
        ['Discount Label', this.formatPercentage(contract.DiscountPercentage)],
        ['Payment Type Label', contract.CustomerRebateSettlement],
        ['Match Contract Label', this.formatPercentage(contract.MatchPercentage)],
      ]
    },
    // Only the discounts: dealers want to see where the private lease discount comes from, the rate
    // and the VWPFS comparison add nothing to that. Operational lease is left out on purpose, most
    // dealers lease through their own captive.
    privateLeaseDiscounts() {
      if (!this.showPrivateLease) return null
      return this.latestVwpfsPrivateLeaseCall?.response?.pricingData?.calculatedProduct?.discounts
    },
    privateLeaseDiscountFields() {
      const { foak, dealer } = this.privateLeaseDiscounts
      return [
        ['Standard Foak Amount Label', this.formatAmount(foak?.standardFoak?.amountExclVat)],
        ['Standard Foak Percentage Label', this.formatPercentage(foak?.standardFoak?.percentage)],
        ['Extra Foak Label', this.formatAmount(foak?.extraFoak?.amountExclVat)],
        ['Extra Foak Amount Label', this.formatAmount(foak?.extraFoakAmount?.amountExclVat)],
        ['Total Foak Label', this.formatAmount(foak?.totalFoakExclVat)],
        ['Dealer Discount Label', this.formatAmount(dealer?.totalExtraDealerDiscountExclVat)],
      ]
    },
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).format('DD-MM-YYYY') : '-'
    },
    formatAmount(value) {
      return value == null || value === '' ? null : CurrencyHelper.currency(value)
    },
    // CMF sends the discount as "3.0".
    formatPercentage(value) {
      if (value == null || value === '') return null
      return `${new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 }).format(value)}%`
    },
    group(key, rows) {
      return {
        title: this.i18n.t(`/Vcr/Autoline/${key} Title`),
        emptyLabel: this.i18n.t(`/Vcr/Autoline/${key} Empty`),
        rows,
        total: ledgerTotal(rows),
      }
    },
    categoryGroup(key, category) {
      return this.group(key, quoteItemRows(this.accessoriesWithCategories(category)))
    },
    // The configuration price is the figure the rest of CST shows, so it wins over the sum of its lines.
    configurationGroup() {
      const group = this.group(
        'Configuration',
        configurationRows(this.configuration, (label) => this.i18n.t(label))
      )
      const price = this.configuration?.Price
      return price ? { ...group, total: toLedgerRow({ price }) } : group
    },
  },
}
</script>

<style lang="scss" src="./vcr-autoline-format.scss"></style>
